import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingCart, Search, Loader2, Trash2, Plus, Minus, Image as ImageIcon,
  Percent, DollarSign, Layers, TrendingDown, Send, Zap, Boxes, X, Award,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { gradeLabel } from "@/lib/grade";
import SlabModal from "@/components/SlabModal";

const CONDITIONS = ["NM", "LP", "MP", "HP", "DMG"];
import { useSubscription } from "@/context/SubscriptionContext";
const GAME_BADGE = { pokemon_japan: "JP", magic: "MTG", onepiece: "OP", riftbound: "RIFT", lorcana: "LOR", yugioh: "YGO" };
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
// Mirror backend CONDITION_MULTIPLIERS (relative to NM market price)
const COND_MULT = { NM: 1.0, LP: 0.9, MP: 0.75, HP: 0.65, DMG: 0.5 };
const condMult = (c) => COND_MULT[c] ?? 1.0;
let rid = 0;

export default function Buying({ boxedOnly = false }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [lotPct, setLotPct] = useState(80);
  const [boxedRate, setBoxedRate] = useState(98);
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [adding, setAdding] = useState(false);
  const [boxedStatus, setBoxedStatus] = useState(null);
  const [catFilter, setCatFilter] = useState("");
  const { gameOptions } = useSubscription();
  const [agreedPay, setAgreedPay] = useState(""); // negotiated flat total you pay the seller
  const [slabOpen, setSlabOpen] = useState(false);

  useEffect(() => {
    api.get("/buying/settings").then(({ data }) => setBoxedRate(data.boxed_rate ?? 98)).catch(() => {});
    api.get("/boxed/status").then(({ data }) => setBoxedStatus(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/catalog/search", { params: { q: query, limit: 20, category: catFilter, boxed_only: boxedOnly } });
        setResults(data); setShowResults(true);
      } catch { /* ignore */ } finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [query, catFilter, boxedOnly]);

  const fetchLiveMarket = async (rowId, productId, printing) => {
    try {
      const { data } = await api.get(`/catalog/live-price/${productId}`, { params: { printing: printing || "Normal", condition: "NM" } });
      const patch = { nm_lowest: data.lowest };
      if (data.market != null) { patch.market = data.market; patch.live = true; }
      update(rowId, patch);
    } catch { /* keep catalog price */ }
  };

  const addItem = (r) => {
    const printing = r.printings?.[0];
    const newRid = ++rid;
    setItems((prev) => [
      ...prev,
      {
        rid: newRid,
        product_id: r.product_id,
        name: r.name,
        set_name: r.set_name,
        number: r.number,
        rarity: r.rarity,
        image_url: r.image_url,
        category: r.category || "pokemon",
        printings: r.printings || [],
        printing: printing?.printing || "",
        market: printing?.market || 0,
        live: false,
        boxed: r.boxed || {},
        condition: "NM",
        qty: 1,
        pct: lotPct,
      },
    ]);
    setQuery(""); setShowResults(false);
    toast.success(`Added ${r.name}`);
    fetchLiveMarket(newRid, r.product_id, printing?.printing || "Normal");
    fetchInvCount(newRid, r.product_id);
  };

  const fetchInvCount = async (rowId, productId) => {
    try {
      const { data } = await api.get("/cards/inventory-count", { params: { product_id: productId } });
      update(rowId, { inv_qty: data.total, inv_by_cond: data.by_condition || {} });
    } catch { /* ignore */ }
  };

  const invBreakdown = (it) => Object.entries(it.inv_by_cond || {}).map(([c, q]) => `${c}: ${q}`).join(" · ");

  const addSlabItem = (s) => {
    setItems((prev) => [...prev, {
      rid: ++rid, is_slab: true, product_id: s.cert_number || `slab-${Date.now()}`,
      name: s.name, set_name: s.set_name, number: s.card_number, rarity: "",
      image_url: s.image_url, category: "pokemon",
      item_type: "slab", grade: s.grade, cert_number: s.cert_number, grading_company: "PSA",
      printings: [], printing: "", market: Number(s.market_value) || 0, live: false, boxed: {},
      condition: "NM", qty: Number(s.quantity) || 1, pct: lotPct,
    }]);
    toast.success(`Added slab ${s.grade || ""} ${s.name}`);
  };

  const changePrinting = (it, value) => {
    const p = it.printings.find((x) => x.printing === value);
    update(it.rid, { printing: value, market: p?.market || 0, live: false });
    fetchLiveMarket(it.rid, it.product_id, value);
    if (it.condition !== "NM") fetchCondOptions(it.rid, it.product_id, value, it.condition);
  };

  const fetchCondOptions = async (rowId, productId, printing, condition) => {
    try {
      const { data } = await api.get(`/catalog/live-price/${productId}`, { params: { printing: printing || "Normal", condition } });
      update(rowId, { cond_median: data.median, cond_lowest: data.lowest,
        basis: data.median != null ? "median" : (data.lowest != null ? "lowest" : "median") });
    } catch { /* ignore */ }
  };

  const changeCondition = (it, value) => {
    const patch = { condition: value };
    if (value === "NM") { patch.cond_median = null; patch.cond_lowest = null; patch.basis = "market"; }
    else { patch.basis = "median"; }
    update(it.rid, patch);
    if (value !== "NM") fetchCondOptions(it.rid, it.product_id, it.printing, value);
  };

  const update = (rid, patch) => setItems((prev) => prev.map((it) => (it.rid === rid ? { ...it, ...patch } : it)));
  const remove = (rid) => setItems((prev) => prev.filter((it) => it.rid !== rid));

  const bumpPct = (it, delta) => update(it.rid, { pct: Math.max(0, Math.min(100, Math.round((Number(it.pct) || 0) + delta))) });

  const applyLotPctToAll = () => {
    setItems((prev) => prev.map((it) => ({ ...it, pct: Number(lotPct) || 0 })));
    toast.success(`Applied ${lotPct}% to all cards`);
  };

  const condMarket = (it) => {
    if (it.is_slab) return Number(it.market) || 0;
    if (it.condition === "NM") {
      if (it.basis === "lowest" && it.nm_lowest != null) return Number(it.nm_lowest);
      return Number(it.market) || 0;
    }
    const basis = it.basis || "median";
    const primary = basis === "lowest" ? it.cond_lowest : it.cond_median;
    const alt = basis === "lowest" ? it.cond_median : it.cond_lowest;
    const chosen = primary != null ? primary : alt;
    if (chosen != null) return Number(chosen);
    return (Number(it.market) || 0) * condMult(it.condition);
  };
  const buyUnit = (it) => condMarket(it) * (Number(it.pct) || 0) / 100;
  const boxedInfo = (it) => (it.condition === "NM" && it.boxed && it.boxed[it.printing]) ? it.boxed[it.printing] : null;
  const boxedOnList = (it) => !!(it.boxed && it.boxed[it.printing]);
  const boxedUnit = (it) => {
    const b = boxedInfo(it);
    return b ? round2((Number(b.market) || 0) * (Number(boxedRate) || 0) / 100) : null;
  };
  const totalMarket = items.reduce((s, it) => s + condMarket(it) * (Number(it.qty) || 0), 0);
  const totalCost = items.reduce((s, it) => s + buyUnit(it) * (Number(it.qty) || 0), 0);
  const totalBoxed = items.reduce((s, it) => { const u = boxedUnit(it); return s + (u != null ? u * (Number(it.qty) || 0) : 0); }, 0);
  const boxedEligibleCost = items.reduce((s, it) => s + (boxedUnit(it) != null ? buyUnit(it) * (Number(it.qty) || 0) : 0), 0);
  const boxedCount = items.filter((it) => boxedUnit(it) != null).reduce((s, it) => s + (Number(it.qty) || 0), 0);
  const totalCards = items.reduce((s, it) => s + (Number(it.qty) || 0), 0);
  const agreedVal = agreedPay !== "" && Number(agreedPay) > 0 && !Number.isNaN(Number(agreedPay)) ? round2(Number(agreedPay)) : null;
  const effPay = agreedVal != null ? agreedVal : totalCost;
  const blendedPct = totalMarket > 0 ? (effPay / totalMarket) * 100 : 0;
  const flipMargin = totalBoxed - boxedEligibleCost;

  const saveBoxedRate = async (val) => {
    const rate = Math.min(100, Math.max(0, Number(val) || 0));
    setBoxedRate(rate);
    try {
      const { data } = await api.post("/buying/settings", { boxed_rate: rate });
      if (data && typeof data.boxed_rate === "number") setBoxedRate(data.boxed_rate);
    } catch { /* ignore */ }
  };

  const commit = async () => {
    if (items.length === 0) { toast.error("Add some cards first"); return; }
    setAdding(true);
    const scale = (agreedVal != null && totalCost > 0) ? agreedVal / totalCost : 1;
    try {
      const payload = {
        items: items.map((it) => it.is_slab ? {
          name: it.name,
          set_name: it.set_name,
          card_number: it.number,
          condition: "NM",
          quantity: Number(it.qty) || 1,
          image_url: it.image_url,
          category: "pokemon",
          item_type: "slab",
          grade: it.grade,
          cert_number: it.cert_number,
          grading_company: "PSA",
          tcg_product_id: it.product_id,
          sku: it.cert_number || it.product_id,
          tcg_printing: "",
          tcg_market_price: round2(condMarket(it)),
          tcg_price: round2(condMarket(it)),
          cost: Number((buyUnit(it) * scale).toFixed(2)),
          buy_pct: Number(it.pct) || 0,
          on_tcgplayer: false,
          price_override: true,
        } : {
          name: it.name,
          set_name: it.set_name,
          card_number: it.number,
          rarity: it.rarity,
          condition: it.condition,
          quantity: Number(it.qty) || 1,
          image_url: it.image_url,
          category: it.category || "pokemon",
          tcg_product_id: it.product_id,
          sku: it.product_id,
          tcg_printing: it.printing,
          tcg_market_price: round2(condMarket(it)),
          tcg_price: round2(condMarket(it)),
          price_basis: it.basis === "lowest" ? "lowest" : (it.basis === "median" ? "median" : ""),
          cost: Number((buyUnit(it) * scale).toFixed(2)),
          buy_pct: Number(it.pct) || 0,
          on_tcgplayer: true,
        }),
      };
      const { data } = await api.post("/staging/bulk", payload);
      toast.success(`Sent ${data.added} cards to Staging for review`);
      setItems([]);
      navigate("/staging");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100">{boxedOnly ? "Boxed.gg — Buylist Buying" : "Buying — Card Show Lot"}</h1>
        <p className="text-sm text-slate-400 mt-1">{boxedOnly
          ? <>Search shows <span className="text-blue-400">only cards Boxed is actively buying</span> right now — see exactly what you can flip to Boxed from a seller's collection.</>
          : <>Search cards to pull the <span className="text-emerald-400">live TCGPlayer market price</span>, set a % of market you'll pay for the lot, and tweak any card individually.</>}</p>
        {boxedStatus && (
          <div className="mt-2 inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-lg bg-blue-950/60 border border-blue-800/50 text-blue-300" data-testid="boxed-status-badge">
            <Zap className="h-3 w-3" />
            {boxedStatus.running ? "Syncing Boxed buylist…" : `Boxed buylist: ${(boxedStatus.count || 0).toLocaleString()} cards${boxedStatus.last_synced ? ` · updated ${new Date(boxedStatus.last_synced).toLocaleDateString()}` : ""}`}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left: search + table */}
        <div className="lg:col-span-3 space-y-5">
          {/* search */}
          <div className="rounded-2xl border p-4" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
            <div className="relative">
              <div className="flex gap-2">
                <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="buying-category-filter"
                  className="px-2.5 py-2.5 rounded-xl border text-sm text-slate-200 outline-none focus:ring-2 focus:ring-amber-500/50 shrink-0" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                  {gameOptions.map(([v, l]) => <option key={v} value={v} style={{ background: "#0D1017" }}>{l}</option>)}
                </select>
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border flex-1 focus-within:ring-2 focus-within:ring-amber-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                  <Search className="h-4 w-4 text-slate-500 shrink-0" />
                  <input value={query} onChange={(e) => setQuery(e.target.value)} onFocus={() => results.length && setShowResults(true)}
                    data-testid="buying-search-input" placeholder="Search by name — add the set for vintage cards (e.g. Charizard Base Set)"
                    className="w-full bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-600" />
                  {searching && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
                </div>
                {!boxedOnly && (
                  <button onClick={() => setSlabOpen(true)} data-testid="buying-add-slab-btn" title="Add a graded slab to the lot by cert number or barcode"
                    className="shrink-0 flex items-center gap-1.5 px-3 rounded-xl border text-sm font-semibold text-violet-300 hover:bg-violet-500/10" style={{ borderColor: "var(--pk-border)" }}>
                    <Award className="h-4 w-4" /> Slab
                  </button>
                )}
              </div>
              {showResults && results.length > 0 && (
                <div className="absolute z-30 mt-2 w-full rounded-xl border max-h-80 overflow-y-auto shadow-2xl" style={{ background: "#0D1017", borderColor: "var(--pk-border)" }} data-testid="buying-results">
                  {results.map((r) => (
                    <button key={r.product_id} onClick={() => addItem(r)} data-testid="buying-result-item"
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[var(--pk-hover)] text-left border-b last:border-0" style={{ borderColor: "var(--pk-border-subtle)" }}>
                      <div className="h-12 w-9 rounded bg-[var(--pk-card)] overflow-hidden shrink-0 flex items-center justify-center border" style={{ borderColor: "var(--pk-border)" }}>
                        {r.image_url ? <img src={r.image_url} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-3 w-3 text-slate-700" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-100 font-medium truncate flex items-center gap-1.5">{r.name}{GAME_BADGE[r.category] && <span className="shrink-0 px-1 py-0.5 rounded bg-indigo-950/80 border border-indigo-800/60 text-[9px] font-bold text-indigo-300">{GAME_BADGE[r.category]}</span>}</div>
                        <div className="text-[11px] text-slate-500 truncate">{r.set_name} · #{r.number} · {r.rarity}</div>
                      </div>
                      {r.boxed && Object.keys(r.boxed).length > 0 && (
                        <span className="shrink-0 px-1.5 py-0.5 rounded bg-blue-950/80 border border-blue-800/60 text-[9px] font-bold text-blue-400" title="On Boxed buylist">BOXED</span>
                      )}
                      {r.printings?.[0]?.market != null && <span className="font-mono-num text-sm text-amber-400 shrink-0">{fmt(r.printings[0].market)}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* table */}
          <div className="rounded-2xl border overflow-hidden" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
            {items.length === 0 ? (
              <div className="py-16 text-center text-slate-500">
                <ShoppingCart className="h-8 w-8 mx-auto mb-3 text-slate-700" />
                {boxedOnly
                  ? "No cards in this lot yet. Search above — only cards Boxed is currently buying will appear."
                  : "No cards in this lot yet. Use the search above to add cards a seller brings you."}
              </div>
            ) : (
              <div className="hidden md:block">
                <table className="w-full text-sm table-fixed" data-testid="buying-lot-table">
                  <colgroup>
                    <col style={{ width: "17%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "17%" }} />
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "4%" }} />
                  </colgroup>
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b" style={{ borderColor: "var(--pk-border)" }}>
                      <th className="px-3 py-3 font-semibold">Card</th>
                      <th className="px-3 py-3 font-semibold">Cond</th>
                      <th className="px-3 py-3 font-semibold">TCG Market</th>
                      <th className="px-3 py-3 font-semibold">Qty</th>
                      <th className="px-3 py-3 font-semibold">% Paid</th>
                      <th className="px-3 py-3 font-semibold text-right">Buy / ea</th>
                      <th className="px-3 py-3 font-semibold text-right">Boxed / ea</th>
                      <th className="px-3 py-3 font-semibold text-right">Line</th>
                      <th className="px-2 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.rid} data-testid="buying-lot-row" className="border-b" style={{ borderColor: "var(--pk-border-subtle)" }}>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-10 w-8 rounded bg-[var(--pk-card)] overflow-hidden shrink-0 flex items-center justify-center border" style={{ borderColor: "var(--pk-border)" }}>
                              {it.image_url ? <img src={it.image_url} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-3 w-3 text-slate-700" />}
                            </div>
                            <div className="min-w-0">
                              <div className="text-slate-100 font-medium truncate">{it.name}</div>
                              <div className="text-[11px] text-slate-500 truncate">{[it.set_name, it.number ? `#${it.number}` : ""].filter(Boolean).join(" · ")}</div>
                              {it.inv_qty != null && (
                                <div data-testid="buying-inv-count" title={it.inv_qty > 0 ? `Already in inventory — ${invBreakdown(it)}` : "Not currently in inventory"}
                                  className={`mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${it.inv_qty > 0 ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "text-slate-500 border-transparent"}`}>
                                  <Boxes className="h-3 w-3" /> {it.inv_qty > 0 ? `${it.inv_qty} in inventory` : "Not in inventory"}
                                </div>
                              )}
                              {it.printings.length > 1 && (
                                <select value={it.printing} onChange={(e) => changePrinting(it, e.target.value)} data-testid="buying-row-printing-select" className="mt-1 text-[10px] bg-transparent border rounded px-1 py-0.5 text-slate-400" style={{ borderColor: "var(--pk-border)" }}>
                                  {it.printings.map((p) => <option key={p.printing} value={p.printing} style={{ background: "#0D1017" }}>{p.printing}</option>)}
                                </select>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top">
                          {it.is_slab ? (
                            <span data-testid="buying-row-grade" className="inline-block px-2 py-1 rounded bg-violet-500/15 text-violet-300 border border-violet-500/40 text-xs font-bold">{gradeLabel(it) || "Slab"}</span>
                          ) : (
                            <select value={it.condition} onChange={(e) => changeCondition(it, e.target.value)}
                              className="w-full bg-[var(--pk-card)] border rounded-lg px-2 py-1 text-xs text-slate-200" style={{ borderColor: "var(--pk-border)" }}>
                              {CONDITIONS.map((c) => <option key={c} value={c} style={{ background: "#0D1017" }}>{c}</option>)}
                            </select>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top" data-testid="buying-row-market">
                          <div className="font-mono-num text-slate-300 inline-flex items-center gap-1.5">{fmt(condMarket(it))}{it.condition === "NM" && it.live && <span title="Live TCGPlayer market price" className="h-1.5 w-1.5 rounded-full bg-emerald-400" data-testid="buying-live-dot" />}</div>
                          {it.condition === "NM" && it.nm_lowest != null && (
                            <div className="flex flex-col gap-1 mt-1.5">
                              {[["market", "Mkt", Number(it.market) || null], ["lowest", "Low", it.nm_lowest]].map(([b, lbl, val]) => {
                                const active = (it.basis || "market") === b;
                                return (
                                  <button key={b} onClick={() => update(it.rid, { basis: b })} disabled={val == null} data-testid={`buying-basis-${b}`}
                                    className={`flex items-center justify-between gap-1 px-1.5 py-0.5 rounded border text-[10px] ${active ? "border-amber-500/60 bg-amber-500/10 text-amber-400" : "text-slate-400"} ${val == null ? "opacity-40 cursor-not-allowed" : ""}`}
                                    style={active ? {} : { borderColor: "var(--pk-border)" }}>
                                    <span>{lbl}</span><span className="font-mono-num">{val != null ? fmt(val) : "—"}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          {it.condition !== "NM" && (it.cond_median != null || it.cond_lowest != null) && (
                            <div className="flex flex-col gap-1 mt-1.5">
                              {[["median", "Med", it.cond_median], ["lowest", "Low", it.cond_lowest]].map(([b, lbl, val]) => {
                                const active = (it.basis || "median") === b;
                                return (
                                  <button key={b} onClick={() => update(it.rid, { basis: b })} disabled={val == null} data-testid={`buying-basis-${b}`}
                                    className={`flex items-center justify-between gap-1 px-1.5 py-0.5 rounded border text-[10px] ${active ? "border-amber-500/60 bg-amber-500/10 text-amber-400" : "text-slate-400"} ${val == null ? "opacity-40 cursor-not-allowed" : ""}`}
                                    style={active ? {} : { borderColor: "var(--pk-border)" }}>
                                    <span>{lbl}</span><span className="font-mono-num">{val != null ? fmt(val) : "—"}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          {it.condition !== "NM" && it.cond_median == null && it.cond_lowest == null && (
                            <div className="text-[10px] text-slate-600 mt-1">× NM mult.</div>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => update(it.rid, { qty: Math.max(1, (Number(it.qty) || 1) - 1) })} className="h-6 w-6 rounded border flex items-center justify-center text-slate-400 hover:text-slate-100" style={{ borderColor: "var(--pk-border)" }}><Minus className="h-3 w-3" /></button>
                            <span className="w-7 text-center font-mono-num text-slate-100">{it.qty}</span>
                            <button onClick={() => update(it.rid, { qty: (Number(it.qty) || 1) + 1 })} className="h-6 w-6 rounded border flex items-center justify-center text-slate-400 hover:text-slate-100" style={{ borderColor: "var(--pk-border)" }}><Plus className="h-3 w-3" /></button>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            <button onClick={() => bumpPct(it, -5)} data-testid="buying-pct-minus" title="-5%" className="h-7 w-6 shrink-0 rounded-lg border flex items-center justify-center text-slate-400 hover:text-amber-400 hover:border-amber-500/40" style={{ borderColor: "var(--pk-border)" }}><Minus className="h-3.5 w-3.5" /></button>
                            <div className="flex items-center justify-center gap-0.5 bg-[var(--pk-card)] border rounded-lg px-1.5 py-1.5 flex-1 min-w-0 focus-within:ring-2 focus-within:ring-amber-500/50" style={{ borderColor: "var(--pk-border)" }}>
                              <input type="number" value={it.pct} onFocus={(e) => e.target.select()} onChange={(e) => update(it.rid, { pct: e.target.value })} data-testid="buying-row-pct"
                                className="w-full min-w-0 bg-transparent outline-none text-sm text-slate-100 font-mono-num text-center" />
                              <span className="text-slate-500 text-xs">%</span>
                            </div>
                            <button onClick={() => bumpPct(it, 5)} data-testid="buying-pct-plus" title="+5%" className="h-7 w-6 shrink-0 rounded-lg border flex items-center justify-center text-slate-400 hover:text-amber-400 hover:border-amber-500/40" style={{ borderColor: "var(--pk-border)" }}><Plus className="h-3.5 w-3.5" /></button>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right font-mono-num text-emerald-400">{fmt(buyUnit(it))}</td>
                        <td className="px-3 py-3 text-right font-mono-num" data-testid="buying-row-boxed">
                          {boxedUnit(it) != null ? (
                            <div>
                              <span className="text-blue-400">{fmt(boxedUnit(it))}</span>
                              {boxedInfo(it)?.needed != null && <span className="block text-[10px] text-slate-500" data-testid="buying-row-needed">needs {boxedInfo(it).needed}</span>}
                            </div>
                          ) : boxedOnList(it) ? (
                            <span className="text-slate-500 text-xs">NM only</span>
                          ) : (
                            <span className="text-slate-600 text-xs">Not on Boxed</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right font-mono-num text-slate-100">{fmt(buyUnit(it) * (Number(it.qty) || 0))}</td>
                        <td className="px-2 py-3 text-right">
                          <button onClick={() => remove(it.rid)} className="h-7 w-7 rounded flex items-center justify-center text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {items.length > 0 && (
              <div className="md:hidden divide-y" style={{ borderColor: "var(--pk-border-subtle)" }}>
                {items.map((it) => (
                  <div key={it.rid} data-testid="buying-lot-row" className="p-4">
                    <div className="flex gap-3">
                      <div className="h-16 w-12 rounded bg-[var(--pk-card)] overflow-hidden shrink-0 flex items-center justify-center border" style={{ borderColor: "var(--pk-border)" }}>
                        {it.image_url ? <img src={it.image_url} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-4 w-4 text-slate-700" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-slate-100 font-semibold truncate">{it.name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{[it.set_name, it.number ? `#${it.number}` : ""].filter(Boolean).join(" · ")}</div>
                        {it.inv_qty != null && (
                          <div data-testid="buying-inv-count" title={it.inv_qty > 0 ? `Already in inventory — ${invBreakdown(it)}` : "Not currently in inventory"}
                            className={`mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${it.inv_qty > 0 ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "text-slate-500 border-transparent"}`}>
                            <Boxes className="h-3 w-3" /> {it.inv_qty > 0 ? `${it.inv_qty} in inventory` : "Not in inventory"}
                          </div>
                        )}
                        {it.printings.length > 1 && (
                          <select value={it.printing} onChange={(e) => changePrinting(it, e.target.value)} className="mt-1 text-[11px] bg-[var(--pk-card)] border rounded px-1.5 py-1 text-slate-300" style={{ borderColor: "var(--pk-border)" }}>
                            {it.printings.map((p) => <option key={p.printing} value={p.printing} style={{ background: "#0D1017" }}>{p.printing}</option>)}
                          </select>
                        )}
                      </div>
                      <button onClick={() => remove(it.rid)} className="h-9 w-9 shrink-0 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-400 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500">Condition</span>
                        {it.is_slab ? (
                          <div className="mt-1"><span className="inline-block px-2 py-1.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/40 text-xs font-bold">{gradeLabel(it) || "Slab"}</span></div>
                        ) : (
                          <select value={it.condition} onChange={(e) => changeCondition(it, e.target.value)}
                            className="mt-1 w-full bg-[var(--pk-card)] border rounded-lg px-2 py-2 text-sm text-slate-200" style={{ borderColor: "var(--pk-border)" }}>
                            {CONDITIONS.map((c) => <option key={c} value={c} style={{ background: "#0D1017" }}>{c}</option>)}
                          </select>
                        )}
                      </label>
                      <div>
                        <span className="text-[10px] uppercase tracking-wider text-slate-500">Quantity</span>
                        <div className="mt-1 flex items-center gap-2">
                          <button onClick={() => update(it.rid, { qty: Math.max(1, (Number(it.qty) || 1) - 1) })} className="h-9 w-9 rounded-lg border flex items-center justify-center text-slate-400" style={{ borderColor: "var(--pk-border)" }}><Minus className="h-4 w-4" /></button>
                          <span className="flex-1 text-center font-mono-num text-slate-100">{it.qty}</span>
                          <button onClick={() => update(it.rid, { qty: (Number(it.qty) || 1) + 1 })} className="h-9 w-9 rounded-lg border flex items-center justify-center text-slate-400" style={{ borderColor: "var(--pk-border)" }}><Plus className="h-4 w-4" /></button>
                        </div>
                      </div>
                      <label className="block">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500">% Paid</span>
                        <div className="mt-1 flex items-center gap-1.5">
                          <button onClick={() => bumpPct(it, -5)} title="-5%" className="h-9 w-9 shrink-0 rounded-lg border flex items-center justify-center text-slate-400 active:bg-amber-500/15" style={{ borderColor: "var(--pk-border)" }}><Minus className="h-4 w-4" /></button>
                          <div className="flex items-center justify-center gap-0.5 bg-[var(--pk-card)] border rounded-lg px-1 py-2 flex-1 min-w-0" style={{ borderColor: "var(--pk-border)" }}>
                            <input type="number" inputMode="numeric" value={it.pct} onFocus={(e) => e.target.select()} onChange={(e) => update(it.rid, { pct: e.target.value })} data-testid="buying-row-pct"
                              className="w-full min-w-0 bg-transparent outline-none text-base text-slate-100 font-mono-num text-center" />
                            <span className="text-slate-500 text-xs">%</span>
                          </div>
                          <button onClick={() => bumpPct(it, 5)} title="+5%" className="h-9 w-9 shrink-0 rounded-lg border flex items-center justify-center text-slate-400 active:bg-amber-500/15" style={{ borderColor: "var(--pk-border)" }}><Plus className="h-4 w-4" /></button>
                        </div>
                      </label>
                      <div>
                        <span className="text-[10px] uppercase tracking-wider text-slate-500">TCG Market</span>
                        <div className="mt-1 py-1.5 font-mono-num text-sm text-slate-300 inline-flex items-center gap-1.5" data-testid="buying-row-market">{fmt(condMarket(it))}{it.condition === "NM" && it.live && <span title="Live TCGPlayer market price" className="h-1.5 w-1.5 rounded-full bg-emerald-400" />}</div>
                        {it.condition === "NM" && it.nm_lowest != null && (
                          <div className="flex gap-1.5 mt-1">
                            {[["market", "Mkt", Number(it.market) || null], ["lowest", "Low", it.nm_lowest]].map(([b, lbl, val]) => {
                              const active = (it.basis || "market") === b;
                              return (
                                <button key={b} onClick={() => update(it.rid, { basis: b })} disabled={val == null} data-testid={`buying-basis-${b}`}
                                  className={`flex-1 flex flex-col items-start px-2 py-1 rounded-lg border text-[11px] ${active ? "border-amber-500/60 bg-amber-500/10 text-amber-400" : "text-slate-400"} ${val == null ? "opacity-40" : ""}`}
                                  style={active ? {} : { borderColor: "var(--pk-border)" }}>
                                  <span className="uppercase tracking-wide text-[9px] text-slate-500">{lbl}</span>
                                  <span className="font-mono-num">{val != null ? fmt(val) : "—"}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {it.condition !== "NM" && (it.cond_median != null || it.cond_lowest != null) && (
                          <div className="flex gap-1.5 mt-1">
                            {[["median", "Med", it.cond_median], ["lowest", "Low", it.cond_lowest]].map(([b, lbl, val]) => {
                              const active = (it.basis || "median") === b;
                              return (
                                <button key={b} onClick={() => update(it.rid, { basis: b })} disabled={val == null} data-testid={`buying-basis-${b}`}
                                  className={`flex-1 flex flex-col items-start px-2 py-1 rounded-lg border text-[11px] ${active ? "border-amber-500/60 bg-amber-500/10 text-amber-400" : "text-slate-400"} ${val == null ? "opacity-40" : ""}`}
                                  style={active ? {} : { borderColor: "var(--pk-border)" }}>
                                  <span className="uppercase tracking-wide text-[9px] text-slate-500">{lbl}</span>
                                  <span className="font-mono-num">{val != null ? fmt(val) : "—"}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: "var(--pk-border-subtle)" }}>
                      <div>
                        <span className="text-[10px] uppercase tracking-wider text-slate-500">Buy / ea · Line</span>
                        <div className="font-mono-num text-sm"><span className="text-emerald-400">{fmt(buyUnit(it))}</span> <span className="text-slate-500">·</span> <span className="text-slate-100">{fmt(buyUnit(it) * (Number(it.qty) || 0))}</span></div>
                      </div>
                      <div className="text-right" data-testid="buying-row-boxed">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500">Boxed / ea</span>
                        <div className="font-mono-num text-sm">
                          {boxedUnit(it) != null ? (
                            <span className="text-blue-400">{fmt(boxedUnit(it))}{boxedInfo(it)?.needed != null && <span className="block text-[10px] text-slate-500" data-testid="buying-row-needed">needs {boxedInfo(it).needed}</span>}</span>
                          ) : boxedOnList(it) ? (
                            <span className="text-slate-500 text-xs">NM only</span>
                          ) : (
                            <span className="text-slate-600 text-xs">Not on Boxed</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: lot controls */}
        <div className="space-y-6">
          <div className="rounded-2xl border p-5" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3"><Percent className="h-3.5 w-3.5" /> Lot percentage</div>
            <p className="text-xs text-slate-500 mb-3">The % of TCGPlayer market you'll pay. Applies to new cards; use the button to push it to every card already in the lot.</p>
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border focus-within:ring-2 focus-within:ring-amber-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
              <input type="number" value={lotPct} onChange={(e) => setLotPct(e.target.value)} data-testid="buying-lot-pct-input"
                className="w-full bg-transparent outline-none text-lg font-bold text-slate-100 font-mono-num" />
              <span className="text-slate-500">%</span>
            </div>
            <button onClick={applyLotPctToAll} data-testid="buying-apply-pct-button" className="mt-3 w-full py-2 rounded-lg border text-xs text-slate-300 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>
              Apply {lotPct || 0}% to all cards
            </button>
            <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--pk-border)" }}>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2"><Zap className="h-3.5 w-3.5 text-blue-400" /> Boxed buylist rate</div>
              <p className="text-[11px] text-slate-500 mb-2">% of Boxed's market price you're paid. Only shown on cards Boxed is actually buying.</p>
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl border focus-within:ring-2 focus-within:ring-blue-500/50" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                <input type="number" min="0" max="100" value={boxedRate} onChange={(e) => setBoxedRate(e.target.value)} onBlur={(e) => saveBoxedRate(e.target.value)}
                  data-testid="buying-boxed-rate-input" className="w-full bg-transparent outline-none text-sm font-bold text-blue-400 font-mono-num" />
                <span className="text-slate-500 text-sm">%</span>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border p-5" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">Lot summary</div>
            <Row icon={Layers} label="Total cards" value={totalCards} color="text-cyan-400" />
            <Row icon={DollarSign} label="Market value" value={fmt(totalMarket)} color="text-slate-200" />
            <Row icon={ShoppingCart} label="You pay" value={fmt(effPay)} color="text-emerald-400" />
            <div className="flex items-center justify-between py-1.5" data-testid="buying-agreed-row">
              <span className="text-sm text-slate-400 flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" />Agreed pay</span>
              <div className="flex items-center gap-1 px-2 py-1 rounded-lg border w-28 focus-within:ring-2 focus-within:ring-emerald-500/50" style={{ background: "var(--pk-card)", borderColor: agreedVal != null ? "rgba(16,185,129,0.5)" : "var(--pk-border)" }} title="Override the total you pay the seller (negotiated flat amount). Each card's cost scales to match.">
                <span className="text-slate-500 text-sm">$</span>
                <input type="number" inputMode="decimal" min="0" value={agreedPay} placeholder={totalCost.toFixed(2)} onFocus={(e) => e.target.select()}
                  onChange={(e) => setAgreedPay(e.target.value)} data-testid="buying-agreed-pay-input"
                  className="w-full bg-transparent outline-none text-sm text-slate-100 font-mono-num text-right" />
                {agreedVal != null && <button onClick={() => setAgreedPay("")} data-testid="buying-agreed-pay-clear" className="text-slate-500 hover:text-rose-400"><X className="h-3.5 w-3.5" /></button>}
              </div>
            </div>
            {agreedVal != null && (
              <div className="flex items-center justify-end -mt-1 mb-1 text-[11px] text-emerald-400/80" data-testid="buying-agreed-implied">
                agreed {fmt(agreedVal)} = {blendedPct.toFixed(1)}% of market (computed {fmt(totalCost)})
              </div>
            )}
            <Row icon={TrendingDown} label="Blended %" value={`${blendedPct.toFixed(1)}%`} color="text-amber-400" />
            <Row icon={Zap} label={`Boxed pays (${boxedRate}%)`} value={fmt(totalBoxed)} color="text-blue-400" />
            <div className="mt-2 pt-3 border-t text-xs text-slate-500" style={{ borderColor: "var(--pk-border)" }} data-testid="buying-profit-line">
              {boxedCount > 0 ? (
                <>Flip {boxedCount} card(s) to Boxed for a margin of <span className={`font-mono-num ${flipMargin >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmt(flipMargin)}</span></>
              ) : (
                <span className="text-slate-600">No cards in this lot are on the Boxed buylist.</span>
              )}
            </div>
            <button onClick={commit} disabled={adding || items.length === 0} data-testid="buying-commit-button"
              className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 disabled:opacity-50">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to Staging
            </button>
          </div>
        </div>
      </div>
      <SlabModal open={slabOpen} onClose={() => setSlabOpen(false)} onCapture={addSlabItem}
        title="Add Slab to Lot" captureLabel="Add to Lot" />
    </div>
  );
}

function Row({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-slate-400 flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" />{label}</span>
      <span className={`font-mono-num font-bold ${color}`}>{value}</span>
    </div>
  );
}
